/**
 * Phototron SDK Type Definitions
 */

export interface TextureUrls {
  albedo: string;
  albedoPreview?: string;
  normal: string;
  normalPreview?: string;
  specular: string;
  specularPreview?: string;
  // Metallic-roughness maps — the SAME baked maps the GLB uses, so the 2D
  // material matches the 3D model. When present the renderer reads roughness
  // directly (instead of inverting the specular/gloss map) and applies
  // metalness; when absent it falls back to the dielectric specular workflow.
  roughness?: string;
  roughnessPreview?: string;
  metalness?: string;
  metalnessPreview?: string;
}

export interface LightingConfig {
  ambientIntensity: number;
  diffuseIntensity: number;
  specularMultiplier: number;
  fresnelIntensity: number;
  accentColor: [number, number, number];
  accentIntensity: number;
}

export interface MaterialViewerConfig {
  textures: TextureUrls;
  lighting?: Partial<LightingConfig>;
  materialScale?: number;
  verticalOffset?: number;
  interactive?: boolean;
  progressiveLoading?: boolean;
  backgroundColor?: string;
}

export interface ModelViewerConfig {
  src: string;
  poster?: string;
  autoRotate?: boolean;
  cameraControls?: boolean;
  arEnabled?: boolean;
  exposure?: number;
  shadowIntensity?: number;
  environmentImage?: string;
}

export interface ShowcaseConfig {
  assetId: string;
  apiKey: string;
  mode: 'click-to-3d' | 'side-by-side' | 'tabs';
  materialConfig?: Partial<MaterialViewerConfig>;
  modelConfig?: Partial<ModelViewerConfig>;
}

/** Per-asset display tuning, written by the desktop app's SDK export. Both are
 *  RELATIVE factors on DEFAULT_LIGHTING (1.0 = unchanged), so the calibrated
 *  texture maps stay untouched measurement data. */
export interface AssetLighting {
  /** Scales the overall albedo brightness (diffuse AND ambient together). */
  lightIntensity?: number;
  /** Scales only the specular highlights — decoupled from albedo brightness. */
  specularIntensity?: number;
}

export interface AssetData {
  id: string;
  name: string;
  lighting?: AssetLighting;
  /** Which PBR workflow the export was baked in. Informational — the 2D
   *  renderer keys off the presence of the roughness/metalness maps, so an
   *  export with a metalness map renders metallic regardless of this field. */
  workflow?: 'metallic' | 'specular';
  textures: {
    albedo: string;
    albedoPreview?: string;
    normal: string;
    normalPreview?: string;
    specular: string;
    specularPreview?: string;
    roughness?: string;
    roughnessPreview?: string;
    metalness?: string;
    metalnessPreview?: string;
  };
  model?: {
    glb: string;
    sizeMb: number;
    /** Google <model-viewer> camera-orbit ("Θdeg Φdeg radius") that frames the
     *  3D model from the same viewpoint as the flat 2D material capture, so the
     *  2D↔3D transition lines up. Derived from the capture camera pose. */
    cameraOrbit?: string;
    /** Vertical field-of-view ("Ndeg") from the capture optics, so the 3D
     *  perspective matches the (telephoto) flat capture. */
    fieldOfView?: string;
    /** Orbit target ("X Y Z" metres) = the object centre from camera-ray
     *  convergence, so framing ignores the GLB bounding box (a floating mesh
     *  scrap can't offset it). */
    cameraTarget?: string;
  };
  metadata: {
    created: string;
    dimensions: {
      width: number;
      height: number;
    };
  };
}

export interface ApiClientConfig {
  apiKey: string;
  baseUrl?: string;
}

export const DEFAULT_LIGHTING: LightingConfig = {
  ambientIntensity: 0.25,
  diffuseIntensity: 0.95,
  specularMultiplier: 1.2,
  fresnelIntensity: 0.4,
  accentColor: [0.0, 0.898, 1.0], // Phototron cyan #00e5ff
  accentIntensity: 0.08,
};

export const DEFAULT_MATERIAL_CONFIG: Omit<MaterialViewerConfig, 'textures'> = {
  lighting: DEFAULT_LIGHTING,
  materialScale: 1.0,
  verticalOffset: 0.0,
  interactive: true,
  progressiveLoading: true,
  backgroundColor: '#050505',
};
